import { useMemo, useState, type ComponentType } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { BarChart, Card, List, ListItem, type CustomTooltipProps } from "@tremor/react";
import { Alert, Button, message, Switch, Typography } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import sortBy from "lodash.sortby";
import uniq from "lodash.uniq";

dayjs.extend(utc);
dayjs.extend(timezone);

import { type TrainerStats } from "@fitness-app/data-models/entities/TrainerStats";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { supabase } from "~/store/initializeStore";
import { exportTrainerChartDataToCsv } from "./exportTrainerChartDataToCsv";
import { fetchTrainerGhostClients } from "./hooks/fetchTrainerGhostClients";
import { useTrainerKpiData } from "./hooks/useTrainerKpiData";
import { TrainerKPICard } from "./TrainerKPICard";
import { getColorClassName, type AvailableChartColorsKeys } from "./utils";

const valueFormatter = (number: number) => `${Intl.NumberFormat("pl").format(number).toString()}`;

const statusColor: Record<string, string> = {
  Aktywni: "bg-emerald-500",
  Zamrożeni: "bg-amber-500",
  Duchy: "bg-fuchsia-500",
  Odnowienia: "bg-green-500",
};

type PayloadItem = {
  category: string;
  value: number;
  index: string;
  color: AvailableChartColorsKeys;
  type?: string;
  payload: {
    archivedEmail?: string[];
    changedTrainerEmail?: string[];
    renewalEmails?: string[];
    [key: string]: unknown;
  };
  name: string;
  archivedEmail?: string[];
  changedTrainerEmail?: string[];
  renewalEmail?: string[];
};

interface ChartTooltipProps {
  active: boolean | undefined;
  payload: PayloadItem[];
  label: string;
}

const ChartTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (active && payload?.length) {
    return (
      <div
        className={cn(
          // base
          "rounded-md border text-sm shadow-md",
          // border color
          "border-gray-200 dark:border-gray-800",
          // background color
          "bg-white dark:bg-gray-950",
        )}
      >
        <div className={cn("border-b border-inherit px-4 py-2")}>
          <p
            className={cn(
              // base
              "font-medium",
              // text color
              "text-gray-900 dark:text-gray-50",
            )}
          >
            {label}
          </p>
        </div>
        <div className={cn("space-y-1 px-4 py-2")}>
          {payload.map(({ value, category, color, name, payload }, index) => (
            <div key={`id-${index}`}>
              <div className="flex items-center justify-between space-x-8">
                <div className="flex items-center space-x-2">
                  <span
                    className={cn(
                      "rounded-tremor-full border-tremor-background shadow-tremor-card dark:border-dark-tremor-background dark:shadow-dark-tremor-card h-3 w-3 shrink-0 border-2",
                      getColorClassName(color, "bg"),
                    )}
                  />

                  <p
                    className={cn(
                      // text color
                      "whitespace-nowrap text-right tabular-nums",
                      "text-gray-500",
                    )}
                  >
                    {name || category}
                  </p>
                </div>
                <p
                  className={cn(
                    // base
                    "whitespace-nowrap text-right font-medium tabular-nums",
                    // text color
                    "text-gray-900 dark:text-gray-50",
                  )}
                >
                  {valueFormatter(value)}
                </p>
              </div>

              {name === "Archiwizacja" && (
                <div className="flex max-w-[200px] flex-wrap gap-1">
                  <span className="text-xs text-gray-500">{payload.archivedEmail?.join(", ")}</span>
                </div>
              )}
              {name === "Zmiana trenera" && (
                <div className="flex max-w-[200px] flex-wrap gap-1">
                  <span className="text-xs text-gray-500">{payload.changedTrainerEmail?.join(", ")}</span>
                </div>
              )}
              {name === "Przedłużenia" && (
                <div className="flex max-w-[200px] flex-wrap gap-1">
                  <span className="text-xs text-gray-500">{payload.renewalEmails?.join(", ")}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export function TrainerMonthClientsChart({
  stats,
  selectedMonth,
  trainerName,
}: {
  stats: TrainerStats[];
  selectedMonth: dayjs.Dayjs;
  trainerName: string;
}) {
  // Get trainer ID from stats
  const trainerId = stats[0]?.trainerId || "";
  const [isExporting, setIsExporting] = useState(false);

  const [showFullYear, setShowFullYear] = useState(false);

  // Fetch KPI data using the new hook
  const {
    kpiByMonth,
    renewalEvents,
    renewalEventsUnavailable,
    isLoading: kpiLoading,
  } = useTrainerKpiData({
    selectedMonth,
    trainerId,
  });

  // Workbook-style rows: either the whole year (like the Excel sheet) or the selected month.
  const { newClientRows, oldClientRows } = useMemo(() => {
    const months = showFullYear
      ? kpiByMonth
      : kpiByMonth.filter((entry) => entry.month === selectedMonth.format("YYYY-MM"));
    return {
      newClientRows: months.map((entry) => ({ month: entry.month, stats: entry.newClients })),
      oldClientRows: months.map((entry) => ({ month: entry.month, stats: entry.oldClients })),
    };
  }, [kpiByMonth, showFullYear, selectedMonth]);

  const renewalsByDate = useMemo(() => {
    return renewalEvents.reduce(
      (acc, renewal) => {
        // Bucket explicitly in Europe/Warsaw — renewal_month is Warsaw-bucketed in SQL, and
        // an admin browsing from another timezone must land on the same daily keys.
        const date = renewal.renewal_start_at
          ? dayjs(renewal.renewal_start_at).tz("Europe/Warsaw").format("YYYY-MM-DD")
          : `${selectedMonth.format("YYYY-MM")}-01`;
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date]!.push(renewal.client_email);
        return acc;
      },
      {} as Record<string, string[]>,
    );
  }, [renewalEvents, selectedMonth]);

  const handleExportCsv = async () => {
    if (!trainerId) {
      void message.error("Brak ID trenera");
      return;
    }

    setIsExporting(true);
    try {
      const ghostClients = await fetchTrainerGhostClients(supabase, selectedMonth, trainerId);
      exportTrainerChartDataToCsv(stats, ghostClients, trainerName, selectedMonth.format("YYYY-MM"), renewalsByDate);
    } catch (error) {
      void message.error("Błąd podczas eksportowania danych");
    } finally {
      setIsExporting(false);
    }
  };

  const chartData = useMemo(() => {
    return sortBy(
      stats.map((stat) => ({
        date: stat.date,
        Aktywni: Math.max(0, stat.totalClients - stat.totalFrozen - stat.totalGhost),
        Zamrożeni: stat.totalFrozen,
        Duchy: stat.totalGhost,
        Odnowienia: renewalsByDate[stat.date]?.length ?? 0,
      })),
      "date",
    );
  }, [renewalsByDate, stats]);

  const chartData2 = useMemo(() => {
    return sortBy(
      stats.map((stat) => ({
        date: stat.date,
        Archiwizacja: stat.metadata.archivedTrainees ?? 0,
        "Zmiana trenera": stat.metadata.assignedToDifferentTrainer ?? 0,
        Przedłużenia: renewalsByDate[stat.date]?.length ?? 0,
        archivedEmail: stat.metadata.archivedTraineesEmails,
        changedTrainerEmail: stat.metadata.assignedToDifferentTrainerEmails,
        renewalEmails: renewalsByDate[stat.date] ?? [],
      })),
      "date",
    );
  }, [renewalsByDate, stats]);

  const totalReduced = useMemo(() => {
    const allRenewalEmails = chartData2
      .map((item) => item.renewalEmails ?? [])
      .flat()
      .filter((email): email is string => Boolean(email));
    const allArchivedEmails = chartData2
      .map((item) => item.archivedEmail ?? [])
      .flat()
      .filter((email): email is string => Boolean(email) && !allRenewalEmails.includes(email));

    const result = chartData2.reduce(
      (acc, item) => ({
        archived: acc.archived + (item.Archiwizacja ?? 0),
        changedTrainer: acc.changedTrainer + (item["Zmiana trenera"] ?? 0),
        renewals: acc.renewals + (item["Przedłużenia"] ?? 0),
      }),
      { archived: 0, changedTrainer: 0, renewals: 0 },
    );

    return {
      changedTrainer: result.changedTrainer,
      archived: uniq(allArchivedEmails).length,
      renewals: uniq(allRenewalEmails).length,
    };
  }, [chartData2]);

  const summaryOfRenewals = useMemo(() => {
    return chartData.reduce((acc, item) => acc + item.Odnowienia, 0);
  }, [chartData]);

  const lastItem = chartData.at(-1);

  return (
    <div className="flex flex-col gap-4">
      {renewalEventsUnavailable && (
        <Alert
          type="warning"
          showIcon
          message="Nie udało się pobrać danych o przedłużeniach (trainer_renewal_kpi_event). Liczby odnowień na tym widoku są niekompletne."
        />
      )}
      <div className="flex justify-end">
        <Button icon={<DownloadOutlined />} onClick={handleExportCsv} loading={isExporting}>
          Eksportuj do CSV
        </Button>
      </div>
      <Card className="w-full">
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">Aktywni klienci</h3>

        <div>
          <BarChart
            data={chartData}
            index="date"
            categories={["Aktywni", "Zamrożeni", "Duchy"]}
            colors={["emerald", "amber", "red"]}
            stack={true}
            valueFormatter={valueFormatter}
            showLegend={false}
            showYAxis={true}
            allowDecimals={false}
            showGridLines={true}
            startEndOnly={false}
            className="mt-8 h-48"
          />
          {lastItem && (
            <List className="mt-2">
              <ListItem key="active">
                <div className="flex items-center space-x-2">
                  <span className={cn(statusColor.Aktywni, "h-0.5 w-3")} aria-hidden={true} />
                  <span>Aktywni</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {valueFormatter(lastItem.Aktywni)}
                </span>
              </ListItem>
              <ListItem key="frozen">
                <div className="flex items-center space-x-2">
                  <span className={cn(statusColor["Zamrożeni"], "h-0.5 w-3")} aria-hidden={true} />
                  <span>Zamrożeni</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {valueFormatter(lastItem.Zamrożeni)}
                </span>
              </ListItem>
              <ListItem key="ghost">
                <div className="flex items-center space-x-2">
                  <span className={cn(statusColor.Duchy, "h-0.5 w-3")} aria-hidden={true} />
                  <span>Duchy</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {valueFormatter(lastItem.Duchy)}
                </span>
              </ListItem>

              <ListItem>
                <Typography.Text type="secondary" className="mt-2 text-sm">
                  * Stan na ostatni dzień z wybranego okresu ({lastItem.date})
                </Typography.Text>
              </ListItem>
            </List>
          )}
        </div>
      </Card>

      <Card className="w-full">
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
          Przedłużenia i usunięcia
        </h3>

        <div>
          <BarChart
            data={chartData2}
            index="date"
            categories={["Archiwizacja", "Zmiana trenera", "Przedłużenia"]}
            colors={["red", "sky", "green"]}
            stack={true}
            valueFormatter={valueFormatter}
            showLegend={false}
            showYAxis={true}
            allowDecimals={false}
            showGridLines={true}
            startEndOnly={false}
            className="mt-8 h-48"
            customTooltip={ChartTooltip as unknown as ComponentType<CustomTooltipProps>}
          />
          {lastItem && (
            <List className="mt-2">
              <ListItem key="archived">
                <div className="flex items-center space-x-2">
                  <span className={cn("bg-red-500", "h-0.5 w-3")} aria-hidden={true} />
                  <span>Zarchiwizowani</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {totalReduced.archived}
                </span>
              </ListItem>
              <ListItem key="changedTrainer">
                <div className="flex items-center space-x-2">
                  <span className={cn("bg-sky-500", "h-0.5 w-3")} aria-hidden={true} />
                  <span>Zmienili trenera</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {totalReduced.changedTrainer}
                </span>
              </ListItem>
              <ListItem key="renewals">
                <div className="flex items-center space-x-2">
                  <span className={cn(statusColor.Odnowienia, "h-0.5 w-3")} aria-hidden={true} />
                  <span>Przedłużenia</span>
                </div>
                <span className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
                  {valueFormatter(summaryOfRenewals)}
                </span>
              </ListItem>
              <ListItem>
                <Typography.Text type="secondary" className="mt-2 text-sm">
                  * Stan na ostatni dzień z wybranego okresu ({lastItem.date})
                </Typography.Text>
              </ListItem>
            </List>
          )}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Typography.Text className="text-sm">
          Pokaż wszystkie miesiące roku {selectedMonth.format("YYYY")}
        </Typography.Text>
        <Switch checked={showFullYear} onChange={setShowFullYear} size="small" />
      </div>
      <TrainerKPICard title="Przedłużenia - nowi klienci" rows={newClientRows} loading={kpiLoading} />
      <TrainerKPICard title="Przedłużenia - stali klienci" rows={oldClientRows} loading={kpiLoading} />
    </div>
  );
}
